import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../services/auth-service';
import { AuthFormCard } from '@/components/AuthFormCard';
import { AuthFormInput } from '@/components/AuthFormInput';
import type { LoginRequest } from '../types/auth';
import './Login.css';

function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginRequest>({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setError('Please fill in all fields.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await authService.login(formData.username, formData.password);
      
      // Check user role and redirect accordingly
      const token = authService.getToken();
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.role === 'Teacher') {
            navigate('/admin/modules');
          } else {
            navigate('/modules');
          }
        } catch (error) {
          navigate('/subjects'); // Default fallback
        }
      } else {
        navigate('/subjects'); // Default fallback
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Invalid credentials.');
      } else if (err.response?.status === 400) {
        setError('Email and password are required.');
      } else {
        setError('An error occurred. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFormCard title="Login">
      <form onSubmit={handleSubmit} className="auth-form compact" autoComplete="off">
        <AuthFormInput
          id="username"
          name="username"
          type="email"
          value={formData.username}
          onChange={handleInputChange}
          disabled={loading}
          placeholder="Email"
          required
          autoComplete="off"
        />

        <AuthFormInput
          id="password"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleInputChange}
          disabled={loading}
          placeholder="Password"
          required
          autoComplete="off"
        />

        {error && (
          <div className="auth-form-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="auth-form-button"
          disabled={loading}
        >
          {loading ? 'Logging in...' : 'Log in'}
        </button>
        <Link className="auth-form-secondary-link" to="/forgot-password">Forgot password?</Link>
      </form>
    </AuthFormCard>
  );
}

export default Login;
